import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '@core/interfaces/apiResponse.interfaces';
import { PaginationResult } from '@core/interfaces/pagination.interface';
import { ProductBusiness } from '@models/productBusiness';
import { appDb } from '@core/db/app-db';
import { ConnectivityService } from '@core/services/connectivity.service';
import { TenantSessionService } from '@core/services/tenant-session.service';
import { JwtService } from '@core/services/jwt.service';
import { shouldUseOfflineFallback, withSyncMetadata } from '@core/services/offline.utils';

const CATALOG_SYNC_KEY = 'catalog_last_synced_at';

// How long (in milliseconds) a locally cached catalog is considered fresh before
// a background re-download is triggered. 4 h by default; can be raised to reduce
// bandwidth usage on large catalogs.
const CATALOG_MAX_AGE_MS = 4 * 60 * 60 * 1000;

// Number of records fetched per request. Kept small enough to avoid blocking the
// main thread during deserialization while still being efficient over the wire.
const SYNC_PAGE_SIZE = 150;

function toNameLower(item: ProductBusiness): string {
  return item.productObject?.name?.toLowerCase().trim() ?? '';
}

@Injectable({ providedIn: 'root' })
export class CatalogSyncService {
  private readonly http = inject(HttpClient);
  private readonly tenantSessionService = inject(TenantSessionService);
  private readonly connectivityService = inject(ConnectivityService);
  private readonly jwtService = inject(JwtService);

  // OFFLINE: Downloads the full product catalog page by page into IndexedDB so
  // that the sales module can search products when there is no network.
  // Skips the download if the stored catalog is still within CATALOG_MAX_AGE_MS.
  async syncCatalogIfStale(): Promise<void> {
    if (!this.connectivityService.isOnline()) {
      return;
    }

    const company = this.tenantSessionService.getCurrentCompany();
    if (!company?.business_id) {
      return;
    }

    const authToken = this.tenantSessionService.getAuthToken();
    if (!authToken) {
      return;
    }

    const lastSync = await this.getLastSyncTimestamp();
    if (lastSync !== null && this.isFresh(lastSync)) {
      return;
    }

    // A stored token past its expiry makes the first page request fail with 401.
    // Renew it first so the download starts with a valid session.
    if (this.jwtService.isTokenExpired(authToken)) {
      const renewal = await firstValueFrom(this.jwtService.refreshToken(authToken));
      if (!renewal.data) {
        return;
      }
      this.jwtService.setAuthToken(renewal.data);
    }

    await this.downloadCatalog(Number(company.business_id));
  }

  private isFresh(isoTimestamp: string): boolean {
    const ageMs = Date.now() - new Date(isoTimestamp).getTime();
    return ageMs < CATALOG_MAX_AGE_MS;
  }

  private async getLastSyncTimestamp(): Promise<string | null> {
    const meta = await appDb.catalog_metadata.get(CATALOG_SYNC_KEY);
    return meta?.value ?? null;
  }

  private async downloadCatalog(businessId: number): Promise<void> {
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      if (!this.connectivityService.isOnline()) {
        break;
      }

      const params = new HttpParams()
        .set('page', page)
        .set('limit', SYNC_PAGE_SIZE);

      let items: ProductBusiness[] = [];

      try {
        const response = await firstValueFrom(
          this.http.get<ApiResponse<PaginationResult<ProductBusiness>>>(
            `${environment.api_rest_url}/productBusiness/business/${businessId}/page`,
            { params },
          ),
        );
        items = response.data?.data ?? [];
      } catch (error) {
        if (shouldUseOfflineFallback(error)) {
          this.connectivityService.markNetworkFailure();
        }
        break;
      }

      if (items.length > 0) {
        // OFFLINE: write transactionally to avoid a corrupt half-sync state if
        // the page loop is interrupted (e.g. tab close, network drop mid-sync).
        await appDb.transaction('rw', appDb.productBusinesses, async () => {
          await appDb.productBusinesses.bulkPut(
            items.map((item) =>
              withSyncMetadata({ ...item, name_lower: toNameLower(item) }),
            ),
          );
        });
      }

      hasMore = items.length === SYNC_PAGE_SIZE;
      page++;
    }

    // Record timestamp only when the loop finished naturally (not interrupted).
    if (hasMore === false) {
      await appDb.catalog_metadata.put({
        key: CATALOG_SYNC_KEY,
        value: new Date().toISOString(),
      });
    }
  }
}
